import { useEffect, type RefObject } from 'react';

/** Default: the two menu-item roles the dashboard dropdowns use. */
const DEFAULT_ITEM_SELECTOR = '[role="menuitem"],[role="menuitemradio"]';

/**
 * Keyboard contract for a `role="menu"` dropdown (WAI-ARIA APG "menu" pattern).
 * A menu that declares `role="menu"` + `role="menuitem"` MUST implement the
 * composite-widget keyboard model — a `<button role="menuitem">` that is just
 * an ordinary tab stop is a broken promise to assistive tech.
 *
 * While `active` (the menu is open), for the items matched by `itemSelector`
 * inside `containerRef`:
 * - on open, focus moves to the FIRST item (roving tabindex → a single Tab stop);
 * - `ArrowDown` / `ArrowUp` cycle focus through the items (wrapping);
 * - `Home` / `End` jump to the first / last item;
 * - exactly one item is tabbable at a time (`tabindex=0`), the rest `-1`.
 *
 * It deliberately does NOT own open/close, Escape, outside-click, or focus
 * RESTORATION — the consuming dropdown already manages those (and they're
 * orthogonal). Activation (Enter/Space/click) stays the browser's job since the
 * items are real `<button>`s.
 *
 * The listener lives on the container (not `document`), so it only fires while
 * focus is within the open menu; it re-queries the item set on every key so a
 * menu whose items change while open (e.g. a filtered list) stays correct.
 */
export function useRovingMenu(
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  itemSelector: string = DEFAULT_ITEM_SELECTOR,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (container === null) return;

    const items = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(itemSelector)).filter(
        (el) => !el.hidden && el.getAttribute('aria-hidden') !== 'true',
      );

    const roveTo = (index: number, els: HTMLElement[]): void => {
      const target = els[index];
      if (target === undefined) return;
      for (const el of els) el.tabIndex = el === target ? 0 : -1;
      target.focus();
    };

    // Initial focus (APG): move focus to the first item on open.
    const initial = items();
    if (initial.length > 0) roveTo(0, initial);

    const onKeyDown = (event: KeyboardEvent): void => {
      const els = items();
      if (els.length === 0) return;
      const current = els.findIndex((el) => el === document.activeElement);
      let next: number;
      switch (event.key) {
        case 'ArrowDown':
          next = current < 0 ? 0 : (current + 1) % els.length;
          break;
        case 'ArrowUp':
          next = current < 0 ? els.length - 1 : (current - 1 + els.length) % els.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = els.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      roveTo(next, els);
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, active, itemSelector]);
}
